import React, { useEffect, useState, useRef, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getProjectBySlug } from "../../api/projectsService";
import SocialShare from "../../components/SocialShare/SocialShare";
import QuickMenu from "../../components/QuickMenu/QuickMenu";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { FaPlay, FaChevronLeft, FaChevronRight, FaExpand, FaHardHat, FaCheck, FaClock } from "react-icons/fa";
const DEFAULT_IMAGE = "/assets/images/example-post.webp";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePhase, setActivePhase] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [playingIndex, setPlayingIndex] = useState(null);

  const sliderRef = useRef(null);
  const videoRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const fetchProject = async () => {
      if (!slug) {
        navigate("/projeler");
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        const res = await getProjectBySlug(slug);
        
        if (res.isSuccess && res.data) {
          const projectData = res.data;
          
          // Determine available phases
          const phases = [];
          if (projectData.beforeImages?.length || projectData.beforeVideos?.length) {
            phases.push('before');
          }
          if (projectData.gallery?.length || projectData.videos?.length || projectData.video) {
            phases.push('during');
          }
          if (projectData.afterImages?.length || projectData.afterVideos?.length) {
            phases.push('after');
          }
          
          // Set default phase
          if (phases.length > 0) {
            setActivePhase(phases.includes('during') ? 'during' : phases[0]);
          }
          
          setProject(projectData);
        } else {
          throw new Error(res.resultMessage || "Proje detayları yüklenemedi");
        }
      } catch (e) {
        console.error(e);
        setError(e.message || "Proje yüklenirken bir hata oluştu");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [slug, navigate]);

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        const origin = (typeof window !== 'undefined')
          ? `&origin=${encodeURIComponent(window.location.origin)}`
          : '';
        return `https://www.youtube.com/embed/${match[1]}?enablejsapi=1&rel=0&playsinline=1&modestbranding=1${origin}`;
      }
    }
    return null;
  };


  const isYoutubeUrl = (url) => {
    return /(?:youtube\.com|youtu\.be)/.test(url);
  };

  const isMp4 = (url) => /\.mp4(\?|#|$)/i.test(url);

  const pauseAllVideos = useCallback(() => {
    // Slider'ın ana list elemanı (klonlar dahil içerir)
    const root =
      sliderRef.current?.innerSlider?.list ||
      document; // fallback

    // Tüm HTML5 videolar (klonlar dahil)
    root.querySelectorAll('video').forEach(v => {
      try { v.pause(); } catch {}
      v.removeAttribute('controls');
    });

    // Tüm YouTube iframeleri (klonlar dahil)
    root.querySelectorAll('iframe[src*="youtube.com/embed"]').forEach(iframe => {
      try {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
          '*'
        );
      } catch {}
    });

    setPlayingIndex(null);
  }, []);

  const forceSlickResize = useCallback(() => {
    try {
      sliderRef.current?.innerSlider?.onWindowResized();
    } catch {}
  }, []);

  useEffect(() => {
    if (activePhase) pauseAllVideos();
  }, [activePhase, pauseAllVideos]);

  useEffect(() => {
    return () => { pauseAllVideos(); };
  }, [pauseAllVideos]);

  const toggleVideo = useCallback(async (idx) => {
    const el = videoRefs.current[idx];
    if (!el) return;

    // Video oynatılıyorsa durdur
    if (playingIndex === idx && !el.paused) {
      try { 
        el.pause(); 
        el.removeAttribute?.('controls');
        setPlayingIndex(null);
      } catch (err) {
        console.error('Video durdurulamadı:', err);
      }
      return;
    }

    // Diğer tüm videoları durdur ve bu videoyu başlat
    pauseAllVideos();
    
    // Küçük bir gecikme ekle
    setTimeout(async () => {
      try {
        el.setAttribute?.('controls', 'controls');
        // Video hazır olana kadar bekle
        if (el.readyState < 2) {
          await new Promise((resolve) => {
            el.addEventListener('canplay', resolve, { once: true });
          });
        }
        const playPromise = el.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
        setPlayingIndex(idx);
      } catch (err) {
        console.error('Video oynatılamadı:', err);
        // Hata durumunda state'i geri al
        el.removeAttribute?.('controls');
        setPlayingIndex(null);
      }
    }, 100);
  }, [pauseAllVideos, playingIndex]);

  const getPhaseData = () => {
    if (!project || !activePhase) return { images: [], videos: [] };

    switch (activePhase) {
      case 'before':
        return {
          images: project.beforeImages || [],
          videos: project.beforeVideos || []
        };
      case 'during':
        const videos = [];
        if (project.videos?.length) videos.push(...project.videos);
        else if (project.video) videos.push(project.video);
        
        return {
          images: project.gallery || [],
          videos: videos
        };
      case 'after':
        return {
          images: project.afterImages || [],
          videos: project.afterVideos || []
        };
      default:
        return { images: [], videos: [] };
    }
  };

  const getAvailablePhases = () => {
    if (!project) return [];
    
    const phases = [];
    
    if (project.beforeImages?.length || project.beforeVideos?.length) {
      phases.push({ key: 'before', label: 'Planlama Aşaması', icon: <FaClock /> });
    }
    
    if (project.gallery?.length || project.videos?.length || project.video) {
      phases.push({ key: 'during', label: 'Yapım Aşaması', icon: <FaHardHat /> });
    }
    
    if (project.afterImages?.length || project.afterVideos?.length) {
      phases.push({ key: 'after', label: 'Bitmiş Hali', icon: <FaCheck /> });
    }
    
    return phases;
  };

  const phaseData = getPhaseData();
  const availablePhases = getAvailablePhases();
  
  // Combine media items
  const mediaItems = [
    ...phaseData.videos.map((video, i) => ({ type: 'video', src: video, index: i })),
    ...phaseData.images.map((image, i) => ({ type: 'image', src: image, index: i }))
  ];

  const hasVideos = (phaseData?.videos?.length || 0) > 0;

  const sliderSettings = {
    dots: true,
    infinite: hasVideos ? false : mediaItems.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: false,
    adaptiveHeight: false,
    beforeChange: () => pauseAllVideos(),
    afterChange: () => pauseAllVideos(),
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      { breakpoint: 768, settings: { arrows: false } }
    ]
  };



  if (loading) {
    return (
      <div className="project-detail-modern">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-detail-modern">
        <div className="error-container">
          <p>{error}</p>
          <Link to="/projeler" className="back-link">← Projelere Dön</Link>
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="project-detail-modern">


      <div className="container">
        <div className="row">
          <QuickMenu />
          
          <div className="col-md-10">
              <div className="project-hero">
                <div className="container">
                  <Link to="/projeler" className="back-button">
                    <FaChevronLeft /> Projelere Dön
                  </Link>
                  
                  <h1 className="project-title">{project.title}</h1>
                  
                  <div className="project-meta">
                    <span className={`status-badge ${project.status?.toLowerCase().includes('devam') ? 'ongoing' : 'completed'}`}>
                      {project.status?.toLowerCase().includes('devam') ? <FaClock /> : <FaCheck />}
                      {project.status || 'Durumu Belirtilmemiş'}
                    </span>
                  </div>

                {project.summary && (
                  <div className="project-summary">
                    <p>{project.summary}</p>
                  </div>
                )}
              </div>
            </div>
            {availablePhases.length > 1 && (
              <div className="phase-selector">
                {availablePhases.map(phase => (
                  <button
                    key={phase.key}
                    className={`phase-button ${activePhase === phase.key ? 'active' : ''}`}
                    onClick={() => {
                      pauseAllVideos();
                      setActivePhase(phase.key);
                    }}
                  >
                    {phase.icon}
                    <span>{phase.label}</span>
                  </button>
                ))}
              </div>
            )}

            {mediaItems.length > 0 && (
              <div className="media-gallery">
                <Slider
                  key={`${activePhase}-${mediaItems.length}`}
                  ref={sliderRef}
                  {...sliderSettings}
                  className="modern-slider"
                >
                  {mediaItems.map((item, index) => (
                    <div className="media-slide" key={index}>
                      {item.type === 'video' ? (
                        isYoutubeUrl(item.src) ? (
                          <div className="media-box">
                            <iframe
                              className="media-el"
                              src={getYoutubeEmbedUrl(item.src)}
                              title={`Video ${index + 1}`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              loading="lazy"
                              onLoad={forceSlickResize}
                            />
                          </div>
                        ) : isMp4(item.src) ? (
                          <div className="media-box">
                            <video
                              className="media-el"
                              ref={el => (videoRefs.current[index] = el)}
                              controls={playingIndex === index}
                              playsInline
                              preload="metadata"
                              onLoadedMetadata={forceSlickResize}
                              onLoadedData={forceSlickResize}
                              onCanPlay={forceSlickResize}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleVideo(index);
                              }}
                              onPlay={() => setPlayingIndex(index)}
                              onPause={() => { if (playingIndex === index) setPlayingIndex(null); }}
                            >
                              <source src={item.src} type="video/mp4" />
                              Tarayıcınız video etiketini desteklemiyor.
                            </video>

                            {playingIndex !== index && (
                              <button
                                className="play-overlay"
                                onClick={(e) => { e.stopPropagation(); toggleVideo(index); }}
                              >
                                <FaPlay />
                              </button>
                            )}
                          </div>
                        ) : null
                      ) : (
                        <div className="media-box">
                          <img
                            className="media-el"
                            src={item.src || DEFAULT_IMAGE}
                            alt={`${project.title} - ${activePhase}`}
                            onError={(e) => (e.target.src = DEFAULT_IMAGE)}
                            onClick={() => { setLightboxIndex(item.index); setIsOpen(true); }}
                          />

                          <button
                            className="expand-button"
                            onClick={() => { setLightboxIndex(item.index); setIsOpen(true); }}
                          >
                            <FaExpand />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </Slider>
              </div>
            )}

            <div className="project-content">
              {typeof project.content === 'string' ? (
                <div 
                  className="project-content-rich-text"
                  dangerouslySetInnerHTML={{ __html: project.content || '' }}
                />
              ) : Array.isArray(project.content) ? (
                project.content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))
              ) : project.content ? (
                <div 
                  className="project-content-rich-text"
                  dangerouslySetInnerHTML={{ __html: String(project.content) }}
                />
              ) : null}
            </div>

            <div className="share-section">
              <SocialShare />
            </div>
          </div>
        </div>
      </div>

      <Lightbox
        open={isOpen}
        close={() => setIsOpen(false)}
        index={lightboxIndex}
        slides={phaseData.images.map(src => ({ src: src || DEFAULT_IMAGE }))}
      />

      <style jsx>{`
        .project-detail-modern {
          min-height: 100vh;
          background: #f8f9fa;
        }

        .loading-container,
        .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 1rem;
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e9ecef;
          border-top-color: #0F75BC;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .project-hero {
          background: linear-gradient(135deg, #0F75BC 0%, #0a5a8f 100%);
          border-radius: 20px;
          color: #fff;
          margin-bottom: 24px;          /* 3rem -> 24px */
          overflow: hidden;
        }

        /* İçerik dikeyde ortalı ve daha az padding */
        .project-hero .container {
          min-height: 180px;            /* küçük ama ferah */
          padding: 18px 24px;           /* daha az iç boşluk */
          display: flex;
          flex-direction: column;
          justify-content: center;      /* DİKEY ORTALAMA */
          align-items: flex-start;
          gap: 10px;
        }

        /* Geri butonu çok yer kaplamasın */
        .back-button {
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          color: #fff;
          text-decoration: none;
          padding: .4rem .85rem;
          background: rgba(255,255,255,.12);
          border-radius: 8px;
          transition: .3s;
          margin: 0;                    /* üst boşluk yok */
        }
        .back-button:hover {
          background: rgba(255,255,255,.22);
          transform: translateX(-4px);
        }

        /* Başlık daha küçük ve sıkı */
        .project-title {
          font-size: 2.05rem;           /* 2.5rem -> 2.05rem */
          font-weight: 700;
          margin: 2px 0 4px;            /* daha az boşluk */
          line-height: 1.2;
        }

        /* Meta satırı daha kompakt */
        .project-meta {
          display: flex;
          align-items: center;
          gap: .75rem;
          margin: 0 0 6px;
        }
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          padding: .45rem .85rem;
          border-radius: 22px;
          font-weight: 600;
        }
        .status-badge.ongoing { background:#ffc107; color:#000; }
        .status-badge.completed { background:#28a745; color:#fff; }

        /* Özet kutusu daha ince */
        .project-summary {
          background: rgba(255,255,255,.10);
          padding: .75rem 1rem;         /* 1.5rem -> .75/1rem */
          border-radius: 10px;
          border-left: 3px solid rgba(255,255,255,.45);
          max-width: 980px;
        }
        .project-summary p {
          margin: 0;
          font-size: 1.02rem;
          line-height: 1.55;
        }

        /* Mobil ayarları */
        @media (max-width: 768px) {
          .project-hero .container {
            min-height: 140px;
            padding: 14px 16px;
            gap: 8px;
          }
          .project-title { font-size: 1.6rem; margin: 0 0 2px; }
          .project-summary { padding: .6rem .8rem; }
          .project-summary p { font-size: .98rem; }
        }


        .phase-selector {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          padding: 1rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .phase-button {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1rem;
          border: 2px solid #e9ecef;
          background: white;
          border-radius: 8px;
          font-weight: 500;
          color: #666;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .phase-button:hover {
          border-color: #0F75BC;
          color: #0F75BC;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(15, 117, 188, 0.15);
        }

        .phase-button.active {
          background: linear-gradient(135deg, #0F75BC 0%, #0a5a8f 100%);
          border-color: #0F75BC;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(15, 117, 188, 0.25);
        }

        .media-gallery {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        }

        .modern-slider .slick-dots {
          bottom: -35px;
        }

        .modern-slider .slick-dots li button:before {
          color: #0F75BC;
          font-size: 12px;
          opacity: 0.3;
        }

        .modern-slider .slick-dots li.slick-active button:before {
          opacity: 1;
        }

        .media-slide {
          outline: none;
        }

        /* Eski tarayıcılar için aspect-ratio fallback */
        @supports not (aspect-ratio: 1 / 1) {
          .video-container.local::before {
            content: "";
            display: block;
            padding-top: 56.25%; /* 16:9 */
          }
          .video-container.local video {
            position: absolute;
            inset: 0;
          }
        }

        /* Tek tip medya kutusu: 16:9, içinde her şey contain */
        .media-box {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          background: #000;
          aspect-ratio: 16 / 9;
        }

        /* İçerideki bütün medya elemanları aynı davranır */
        .media-el {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          display: block;
          object-fit: contain;   /* Görüntü/video taşmadan sığsın */
        }

        /* iFrame object-fit desteklemese de full-fill ile kutuyu dolduruyor */
        .media-box iframe.media-el {
          border: 0;
        }

        /* Eski tarayıcı fallback */
        @supports not (aspect-ratio: 1 / 1) {
          .media-box::before {
            content: "";
            display: block;
            padding-top: 56.25%; /* 16:9 */
          }
          .media-el {
            position: absolute;
            inset: 0;
          }
        }

        /* Play/Expand butonların yeni kutuyla uyumu */
        .play-overlay {
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 80px; height: 80px; border-radius: 50%;
          background: rgba(15, 117, 188, 0.9);
          border: none; color: #fff; font-size: 24px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all .3s ease;
        }

        .play-overlay:hover { background: rgba(15,117,188,1); transform: translate(-50%,-50%) scale(1.1); }

        .expand-button {
          position: absolute; bottom: 1rem; right: 1rem;
          width: 40px; height: 40px; border-radius: 8px;
          background: rgba(15, 117, 188, 0.9);
          border: none; color: white;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all .3s ease;
        }
        .expand-button:hover { background: rgba(15,117,188,1); transform: scale(1.1); }

        /* Mobilde güvenli yükseklik sınırı istersen (opsiyonel) */
        @media (max-width: 768px) {
          .media-box { max-height: 50vh; }
        }
          
        .play-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(15, 117, 188, 0.9);
          border: none;
          color: white;
          font-size: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .play-overlay:hover {
          background: rgba(15, 117, 188, 1);
          transform: translate(-50%, -50%) scale(1.1);
        }

        .expand-button {
          position: absolute;
          bottom: 1rem;
          right: 1rem;
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: rgba(15, 117, 188, 0.9);
          border: none;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .expand-button:hover {
          background: rgba(15, 117, 188, 1);
          transform: scale(1.1);
        }

        .project-content {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .project-content p {
          font-size: 1.1rem;
          line-height: 1.8;
          color: #333;
          margin-bottom: 1.5rem;
        }

        .project-content p:last-child {
          margin-bottom: 0;
        }

        .project-content-rich-text {
          font-size: 1.1rem;
          line-height: 1.8;
          color: #333;
          width: 100%;

          // Headers
          h1, h2, h3, h4, h5, h6 {
            font-weight: 600;
            margin-top: 1rem;
            margin-bottom: 0.5rem;
            color: #1a1a1a;
          }

          h1 { font-size: 2rem; }
          h2 { font-size: 1.75rem; }
          h3 { font-size: 1.5rem; }
          h4 { font-size: 1.25rem; }
          h5 { font-size: 1.1rem; }
          h6 { font-size: 1rem; }

          // Paragraphs
          p {
            margin-bottom: 1rem;
            line-height: 1.8;
          }

          // Lists
          ul, ol {
            margin: 1rem 0;
            padding-left: 2rem;
            
            li {
              margin-bottom: 0.5rem;
              line-height: 1.6;
            }
          }

          // Links
          a {
            color: #3B82F6;
            text-decoration: underline;
          }

          a:hover {
            color: #2563eb;
          }

          // Images
          img {
            max-width: 100%;
            height: auto;
            margin: 1rem 0;
            border-radius: 8px;
          }

          // Blockquotes
          blockquote {
            border-left: 4px solid #3B82F6;
            padding-left: 1rem;
            margin: 1rem 0;
            font-style: italic;
            color: #666;
          }

          // Code blocks
          pre {
            background: #f4f4f4;
            padding: 1rem;
            border-radius: 4px;
            overflow-x: auto;
            margin: 1rem 0;
          }

          code {
            background: #f4f4f4;
            padding: 0.2rem 0.4rem;
            border-radius: 3px;
            font-family: monospace;
          }

          // Text formatting
          strong, b {
            font-weight: 600;
          }

          em, i {
            font-style: italic;
          }

          u {
            text-decoration: underline;
          }

          s, strike {
            text-decoration: line-through;
          }
        }

        .share-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        @media (max-width: 768px) {
          .project-title {
            font-size: 1.8rem;
          }

          .phase-selector {
            flex-direction: column;
          }

          .phase-button {
            width: 100%;
          }

          .media-gallery {
            padding: 1rem;
          }

          .project-content {
            padding: 1rem;
          }
        }
        /* Görsel/video yüksekliğini mobilde güvenli tut */
        .video-container.youtube,
        .image-container img { max-height: 50vh; }

        /* ---------------- MOBILE ---------------- */
        @media (max-width: 768px) {
          .project-detail-modern { overflow-x: hidden; }
          .project-detail-modern * { box-sizing: border-box; }

          /* İçerideki ana kolon mobilde küçülebilsin, taşmasın */
          .project-detail-modern .col-md-10 { min-width: 0; }

          /* Ana kutuların genişliği asla viewport'u aşmasın */
          .media-gallery, .project-content, .share-section { width: 100%; max-width: 100%; }

          // /* Slick'in mobilde yükseklik/genişlik hesaplamasını garantiye al */
          // .modern-slider,
          // .modern-slider .slick-list,
          // .modern-slider .slick-track,
          // .media-slide { width: 100%; }
          // .modern-slider .slick-list { overflow: visible; } /* play/expand taşmasın */
          /* HERO daha kompakt; iç .container genişliği kilitleme */
          .project-hero { border-radius: 12px; margin-bottom: 14px; }
          .project-hero .container { max-width: 100%; padding: 12px 14px !important; gap: 6px; min-height: auto; }

          .project-title { font-size: clamp(1.28rem, 5.2vw, 1.55rem); line-height: 1.22; margin: 0 0 2px; }
          .project-meta { gap: .5rem; margin: 0 0 4px; }
          .status-badge { padding: .35rem .6rem; border-radius: 16px; font-size: .85rem; }

          .project-summary { padding: .55rem .7rem; border-radius: 8px; }
          .project-summary p { font-size: .95rem; line-height: 1.45; }

          /* PHASE SELECTOR: 2 sütunlu grid + metin kesme */
          .phase-selector {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: .6rem;
            padding: .6rem;
            margin-bottom: 12px;
          }
          .phase-button {
            min-width: 0;                /* TAŞMAYI DURDURUR */
            padding: .65rem .6rem;
            border-radius: 8px;
            gap: .45rem;
            font-size: .92rem;
          }
          .phase-button span {
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;     /* uzun başlıkları kırp */
            white-space: nowrap;
          }
          .phase-button svg { font-size: 1rem; }

          /* GALERİ: görünür ve kompakt */
          .media-gallery { padding: .9rem; margin-bottom: 12px; }

          /* İÇERİK: sağa taşmayı bitir */
          .project-content { padding: 12px; margin-bottom: 12px; overflow-wrap: anywhere; }

          /* PAYLAŞ: görünür ve dar padding */
          .share-section { padding: 10px 12px; display: block; 
      `}</style>
    </div>
  );
}

// Custom Arrow Components
function CustomPrevArrow(props) {
  const { onClick } = props;
  return (
    <button
      className="custom-arrow prev"
      onClick={onClick}
      style={{
        position: 'absolute',
        left: '-40px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: 'white',
        border: '2px solid #0F75BC',
        color: '#0F75BC',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#0F75BC';
        e.currentTarget.style.color = 'white';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'white';
        e.currentTarget.style.color = '#0F75BC';
      }}
    >
      <FaChevronLeft />
    </button>
  );
}

function CustomNextArrow(props) {
  const { onClick } = props;
  return (
    <button
      className="custom-arrow next"
      onClick={onClick}
      style={{
        position: 'absolute',
        right: '-40px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: 'white',
        border: '2px solid #0F75BC',
        color: '#0F75BC',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#0F75BC';
        e.currentTarget.style.color = 'white';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'white';
        e.currentTarget.style.color = '#0F75BC';
      }}
    >
      <FaChevronRight />
    </button>
  );
}